import { Button, TextLink } from '@edsn/pie-components';
import AddIcon from '@iconify/icons-material-symbols/add';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCurrentOrganisation } from '#pie/auth';
import { useUserHasRole } from '#pie/auth/useUserHasRole';
import { Dialog } from '#pie/components/layout/dialog/Dialog';
import {
  AuthorizationRoles,
  type CreateContactGroupMembershipRequest,
  getCprManagementContacts,
  getManagementContacts,
  useCreateContactGroupMembership,
  useGetContact,
  useGetCprOrganisations,
} from '#pie/internal/api/pie-bff';
import { typedFormFields } from '#pie/utils/typedFormFields';
import { FormContactCombobox } from '../contact-combobox/FormContactCombobox';
import { CreateCprContactModal } from '../contact-modal/CreateCprContactModal';
import { Icon } from '../icon/Icon';
import type { Option } from '../input-dropdown/InputDropdown';
import { Stack } from '../stack/Stack';
import { useToast } from '../toast/ToastContext';

interface AddCprContactModalProps {
  contactGroupId: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const { FormDropdown } = typedFormFields<CreateContactGroupMembershipRequest>();

export const AddCprContactModal = ({
  contactGroupId,
  open,
  onOpenChange,
}: AddCprContactModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenDialog, setIsOpenDialog] = useState(false);

  const { currentAccount } = useCurrentOrganisation();
  const canAddContact = useUserHasRole({ role: AuthorizationRoles.CprAdmin });
  const subOrganisations = currentAccount?.subOrganisations;
  const { data: organisations } = useGetCprOrganisations({
    pagination: { pageIndex: 0, pageSize: 100 },
    filter: {},
  });
  const subOrganisationsWithEan13 = subOrganisations?.map(
    (subOrganisation) => ({
      ...subOrganisation,
      ean13: organisations?.items.find(
        ({ organisation }) => organisation.id === subOrganisation.id,
      )?.organisation.ean13,
    }),
  );

  const isPrimaryOrganisation = (ean13?: string) =>
    !!ean13 && ean13 === currentAccount?.ean13;

  const { data: contacts, isPending: isContactsLoading } = useGetContact();
  const { addToast } = useToast();
  const showAdministrativeDropdown =
    subOrganisations && subOrganisations.length > 1;
  const dialogOpen = open ?? isOpen;

  const handleOpenChange = (nextOpen: boolean) => {
    if (open === undefined) {
      setIsOpen(nextOpen);
    }

    onOpenChange?.(nextOpen);
  };

  const subOrganisationsOptions: Option[] =
    subOrganisationsWithEan13?.map((item) => ({
      label: item.label,
      value: item.id,
      statusBadge: isPrimaryOrganisation(item.ean13)
        ? {
            className: 'ml-auto shrink-0',
            status: 'completed',
            children: t('common.tag.primary'),
          }
        : undefined,
    })) ??
    /* istanbul ignore next -- @preserve */
    [];

  const formMethods = useForm<CreateContactGroupMembershipRequest>({
    defaultValues: { contactGroupId },
  });

  const { handleSubmit, setValue, watch } = formMethods;

  const selectedContactId = watch('contactId');
  const selectedOrganisationId = watch('organisationId');
  const targetOrganisationId =
    selectedOrganisationId ??
    (subOrganisations?.length === 1 ? subOrganisations[0].id : undefined);

  const { mutate, isPending: isPosting } = useCreateContactGroupMembership({
    mutation: {
      onError: (error) => {
        addToast({
          message: t('cpr_contact_group_detail.add_user.toast.error.message'),
          title: t('cpr_contact_group_detail.add_user.toast.error.title'),
          type: 'error',
          error: error,
        });
      },
      onSuccess: (_data, variables) => {
        addToast({
          message: t(
            'cpr_contact_group_detail.add_user.toast.success.message',
            {
              name: contacts?.items.find(
                (item) => item.id === variables.data.contactId,
              )?.fullName,
            },
          ),
          title: t('cpr_management_group_detail.toast.success.title'),
          type: 'success',
        });

        handleOpenChange(false);
        queryClient.invalidateQueries({
          queryKey: [getCprManagementContacts],
        });
        queryClient.invalidateQueries({
          queryKey: [getManagementContacts],
        });
        queryClient.invalidateQueries({
          queryKey: ['/cpr-management-contact'],
        });
        formMethods.reset();
      },
    },
  });

  const onSubmit: SubmitHandler<CreateContactGroupMembershipRequest> = ({
    organisationId,
    ...data
  }) => {
    const firstOrganisationId = subOrganisations?.[0]?.id;

    // TODO: Find a way to test this
    /* istanbul ignore next -- @preserve */
    if (!organisationId && !firstOrganisationId) {
      throw new Error(`organisationId can't be empty`);
    }

    mutate({
      data: {
        ...data,
        contactGroupId,
        organisationId: organisationId || firstOrganisationId,
      },
    });
  };
  const isLoading = isPosting || isContactsLoading;

  return (
    <>
      {canAddContact && open === undefined && (
        <Button
          action="secondary"
          emphasis="bold"
          iconStart={<Icon icon={AddIcon} />}
          onClick={() => {
            setIsOpenDialog(false);
            handleOpenChange(true);
          }}
        >
          {t('common.contactperson')}
        </Button>
      )}

      {dialogOpen && (
        <Dialog
          title={t('cpr_management.contact_modal.button.add')}
          size="lg"
          open={dialogOpen}
          onOpenChange={handleOpenChange}
        >
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack gap="md">
                {showAdministrativeDropdown && (
                  <FormDropdown
                    options={subOrganisationsOptions}
                    label={t('common.subunit')}
                    name="organisationId"
                    fullWidth
                    onChange={() => setValue('contactId', '')}
                    isLoading={isLoading}
                    className="!data-[state=closed]:text-content-disabled"
                    placeholder={t(
                      'cpr_management_group_detail.modal.add_contact.subunit.placeholder',
                    )}
                  />
                )}
                <FormContactCombobox
                  key={targetOrganisationId}
                  label={t('cpr_management.contact_modal.label_contact')}
                  name="contactId"
                  fullWidth
                  contactGroupId={contactGroupId}
                  organisationId={targetOrganisationId}
                  placeholder={t(
                    'cpr_management_group_detail.modal.add_contact.placeholder',
                  )}
                />
                {!selectedContactId && (
                  <TextLink
                    className="flex justify-start"
                    iconStart={<Icon icon={AddIcon} />}
                    onClick={() => {
                      handleOpenChange(false);
                      setIsOpenDialog(true);
                    }}
                  >
                    {t('cpr_management_group_detail.modal.add_contact.button')}
                  </TextLink>
                )}
                <div className="flex flex-row gap-4">
                  <Button
                    onClick={() => handleOpenChange(false)}
                    type="button"
                    action="tertiary"
                    emphasis="subtle"
                    className="w-full"
                  >
                    {t('common.button.cancel')}
                  </Button>
                  <Button
                    isLoading={isLoading}
                    disabled={isLoading || !selectedContactId}
                    type="submit"
                    className="w-full"
                  >
                    {t('cpr_management_group_detail.button.add')}
                  </Button>
                </div>
              </Stack>
            </form>
          </FormProvider>
        </Dialog>
      )}
      <CreateCprContactModal
        open={isOpenDialog}
        onOpenChange={setIsOpenDialog}
        contactGroupId={contactGroupId}
        onFlowBack={() => handleOpenChange(true)}
      />
    </>
  );
};
